/**
 * Role and route based access control.
 *
 * The roles mirror the `user_role` enum in the API's identity migration
 * (packages/db/migrations/0001_identity.sql). Anything not in that enum cannot
 * come back on a session user, so it does not belong here either.
 *
 * The admin endpoints this console reads are themselves restricted to
 * admin/owner by `requireRole` on the server. These checks decide what to
 * *render*; they are a usability measure, never the enforcement boundary.
 */

import { paths } from './paths'

export const ROLES = {
  owner: 'owner',
  admin: 'admin',
  member: 'member',
  viewer: 'viewer',
}

/** Roles the server will accept on /v1/admin/*. */
export const ADMIN_ROLES = [ROLES.owner, ROLES.admin]

/**
 * Roles that may spend quota.
 *
 * Viewer is excluded on purpose: a read-only role should not be shown a
 * composer whose only outcome is consuming someone's allowance. The API
 * enforces entitlement and quota regardless; this just avoids offering a
 * form that cannot succeed.
 */
export const GENERATING_ROLES = [ROLES.owner, ROLES.admin, ROLES.member]

/**
 * Where a user goes when they have not asked for anywhere in particular —
 * after signing in, and from the root path.
 *
 * Role-aware on purpose: the usage report is admin-only, so sending everyone
 * there means a member's first screen after signing in is "Access denied".
 */
export const landingPathFor = (user) => {
  // Deliberately NOT the dashboard, even for admins: an existing test records
  // the intent that anyone who can generate lands in the studio. The
  // dashboard is a nav entry, not a redirect that overrides that choice.
  if (GENERATING_ROLES.includes(user?.role)) return paths.studio
  return paths.models
}

/** Owners bypass every check. */
const isOwner = (user) => user?.role === ROLES.owner

const getAccessPaths = (user) =>
  (user?.access_control ?? []).map((entry) => entry?.path).filter(Boolean)

/**
 * A route is allowed when it declares no roles, when the user's role is listed,
 * or when the user has an explicit per-path grant.
 */
export const hasRouteAccess = (route, user) => {
  if (!route.roles && !route.requiresAccessPath) return true
  if (!user) return false
  if (isOwner(user)) return true

  const byRole = route.roles?.includes(user.role) ?? false
  const byPath = getAccessPaths(user).includes(route.path)

  return byRole || byPath
}

/**
 * Recursively drops routes the user cannot reach. A parent that becomes empty is
 * dropped with its children, so no layout renders without content.
 */
export const filterRoutesByAccess = (routes, user) =>
  routes.reduce((allowed, route) => {
    const children = route.children ? filterRoutesByAccess(route.children, user) : undefined
    if (route.children && children.length === 0) return allowed
    if (!hasRouteAccess(route, user)) return allowed

    allowed.push(children ? { ...route, children } : route)
    return allowed
  }, [])

/** Navigation entries the user may see, flattened out of the route tree. */
export const getNavRoutes = (routes, user, section = 'main') => {
  const items = []

  const walk = (nodes) => {
    for (const route of nodes) {
      // Now enforced: the session user carries a real role from /v1/auth/me,
      // so a member never sees a nav entry that would 403 on arrival.
      if (!hasRouteAccess(route, user)) continue

      if (route.showInNav && route.path) {
        items.push({
          to: route.path,
          label: route.name,
          icon: route.icon,
          section: route.section ?? 'main',
        })
      } else if (route.children) {
        walk(route.children)
      }
    }
  }

  walk(routes)
  return items.filter((item) => item.section === section)
}
